import { FunnelType } from '@/@types/Upsell';
import { ColorPickerCustom as ColorPicker } from '@/components/shared/ColorPicker/ColorPicker';
import { ComboBox } from '@/components/shared/ComboBox/ComboBox';
import { Input } from '@/components/shared/Input/Input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { OffersService } from '@/services/offers.service';
import { ProductsService } from '@/services/products.service';
import useModalStore from '@/store/useModalStore';
import { normalizeUrlWhenPaste } from '@/utils/normalize';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Info, Loader2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUpsellSchema } from '../schemas/formSchemas';

interface PrefilledData {
  funnel_type?: 'up_sell' | 'down_sell';
  product_id?: string;
  offer_id?: string;
}

interface AddUpsellSheetProps {
  productId: string;
  onAdd: (data: any) => Promise<any>;
  isLoading: boolean;
  prefilledData?: PrefilledData | null;
}

export function AddUpsellSheet({ productId, onAdd, isLoading, prefilledData }: AddUpsellSheetProps) {
  const { t } = useTranslation(['upsells', 'common']);
  const { closeModal } = useModalStore();
  const { getStepSchema } = useUpsellSchema();
  const [step, setStep] = useState(1);
  const totalSteps = 4;

  const [offerSelected, setOfferSelected] = useState({ name: '', price: '' });

  const defaultValues = {
    name: '',
    funnel_type: (prefilledData?.funnel_type || FunnelType.UpSell) as 'up_sell' | 'down_sell',
    page: '',
    product_id: prefilledData?.product_id || productId || '',
    offer_id: prefilledData?.offer_id || '',
    accepted_page: '',
    recused_page: '',
    up_sell_display_text: t('form_placeholders.default_accept', 'Sim, quero essa oferta!'),
    up_sell_button_color: '#5CCE5E',
    up_sell_display_text_color: '#000000',
    down_sell_display_text: t('form_placeholders.default_decline', 'Não, obrigado'),
    down_sell_button_color: '#DC2626',
    down_sell_display_text_color: '#FFFFFF',
  };

  // Usar schema por step — igual ao CreateUpsell.tsx — para evitar validação antecipada
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    clearErrors,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(getStepSchema(step)),
    defaultValues,
  });

  // Armazenar todos os valores acumulados entre steps
  const [allFormData, setAllFormData] = useState(defaultValues);

  useEffect(() => {
    if (prefilledData) {
      if (prefilledData.funnel_type) {
        setValue('funnel_type', prefilledData.funnel_type);
      }
      if (prefilledData.product_id) {
        setValue('product_id', prefilledData.product_id);
      }
      if (prefilledData.offer_id) {
        setValue('offer_id', prefilledData.offer_id);
      }
      // Resetar para o step 1 sempre que dados pre-preenchidos mudarem
      setStep(1);
    }
  }, [prefilledData, setValue]);

  const formValues = watch();

  async function fetchProductOptions(query: string) {
    return await ProductsService.getProductsUpSellOptions(productId, query);
  }

  const selectedProductId = watch('product_id');

  const fetchOfferOptions = async (query: string) => {
    if (!selectedProductId) return [];
    return await OffersService.getUpsellsOffers({
      productId: selectedProductId,
      q: query || '',
    });
  };

  const nextStep = async () => {
    const isValid = await trigger();
    if (isValid) {
      // Acumular valores do step atual antes de avançar
      setAllFormData((prev) => ({ ...prev, ...getValues() }));
      setStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && step < totalSteps) {
      e.preventDefault();
      nextStep();
    }
  };

  const onSubmit = async (data: any) => {
    if (step < totalSteps) {
      nextStep();
      return;
    }
    try {
      // Mesclar valores acumulados com os do step atual
      const finalData = {
        ...allFormData,
        ...data,
        accepted_page: (allFormData.accepted_page || data.accepted_page) || undefined,
        recused_page: (allFormData.recused_page || data.recused_page) || undefined,
      };

      await onAdd(finalData);
      reset();
      setAllFormData(defaultValues);
      setStep(1);
      // Não fechar o modal aqui — o handleCreateUpsell em FunnelMap já faz isso
    } catch (error: any) {
      toast({
        variant: 'error',
        description: error?.response?.data?.message || t('common:data_submission_error'),
      });
    }
  };

  const handleClose = () => {
    reset();
    setAllFormData(defaultValues);
    setStep(1);
    closeModal('funnel-add-upsell');
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <Input
              label={t('form.name')}
              helperText={errors.name?.message}
              {...register('name')}
              placeholder={t('form.name_placeholder')}
            />

            <div>
              <Label className="text-sm mb-1.5 block">{t('form.type')}</Label>
              <Select
                onValueChange={(value) => setValue('funnel_type', value as 'up_sell' | 'down_sell')}
                defaultValue={formValues.funnel_type}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('form.type_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FunnelType.UpSell}>{t('form.type_upsell')}</SelectItem>
                  <SelectItem value={FunnelType.DownSell}>{t('form.type_downsell')}</SelectItem>
                </SelectContent>
              </Select>
              {errors.funnel_type && (
                <p className="text-sm text-red-500 mt-1">{errors.funnel_type.message}</p>
              )}
            </div>

            <Input
              label={t('form.page')}
              helperText={errors.page?.message}
              {...register('page')}
              placeholder={t('form.page_placeholder')}
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://';
                  setValue('page', 'https://');
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e);
                setValue('page', finalValue);
              }}
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <ComboBox
              fetchOptions={fetchProductOptions}
              value={formValues.product_id}
              onChange={(value: any) => {
                clearErrors(['product_id', 'offer_id']);
                setValue('product_id', value.value);
                setValue('offer_id', '');
                setOfferSelected({ name: value.label, price: '' });
              }}
              returnFullObject
              label={t('form.product')}
              placeholder={t('form.product_placeholder')}
              helperText={errors.product_id?.message}
            />
            <ComboBox
              key={formValues.product_id}
              fetchOptions={fetchOfferOptions}
              value={formValues.offer_id}
              onChange={(value: any) => {
                clearErrors('offer_id');
                setValue('offer_id', value.value);
                setOfferSelected({ name: offerSelected.name, price: value.price });
              }}
              returnFullObject
              label={t('form.offer')}
              placeholder={t('form.offer_placeholder')}
              helperText={errors.offer_id?.message}
              queryKeyExtra={[formValues.product_id]}
              disabled={!selectedProductId}
            />
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <Input
              label={t('form.accepted_page')}
              helperText={errors.accepted_page?.message}
              {...register('accepted_page')}
              placeholder="https://exemplo.com/proximo-upsell"
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://';
                  setValue('accepted_page', 'https://');
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e);
                setValue('accepted_page', finalValue);
              }}
            />

            <Input
              label={t('form.recused_page')}
              helperText={errors.recused_page?.message}
              {...register('recused_page')}
              placeholder="https://exemplo.com/downsell"
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://';
                  setValue('recused_page', 'https://');
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e);
                setValue('recused_page', finalValue);
              }}
            />

            <div className="bg-[#18181B] p-4 rounded-md flex items-center">
              <div className="flex gap-3">
                <Info size={20} className="text-white flex-none" />
                <span className="text-muted-foreground text-sm leading-relaxed">
                  {t('form.upsell_description')}
                </span>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <p className="text-xs font-semibold text-emerald-400">
                  {t('accept_button_label', 'Botão Aceitar')}
                </p>
              </div>
              <Input
                label={t('form.up_sell_display_text')}
                helperText={errors.up_sell_display_text?.message}
                {...register('up_sell_display_text')}
                placeholder={t('form.upsell_accept_placeholder')}
              />

              <div className="grid grid-cols-2 gap-4">
                <ColorPicker
                  defaultColor={formValues.up_sell_button_color}
                  onValueChange={(value) => setValue('up_sell_button_color', value)}
                  label={t('form.color_bg')}
                />
                <ColorPicker
                  defaultColor={formValues.up_sell_display_text_color}
                  onValueChange={(value) => setValue('up_sell_display_text_color', value)}
                  label={t('form.color_text')}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="h-2 w-2 rounded-full bg-rose-500" />
                <p className="text-xs font-semibold text-rose-400">
                  {t('decline_button_label', 'Botão Recusar')}
                </p>
              </div>
              <Input
                label={t('form.down_sell_display_text')}
                helperText={errors.down_sell_display_text?.message}
                {...register('down_sell_display_text')}
                placeholder={t('form.upsell_decline_placeholder')}
              />

              <div className="grid grid-cols-2 gap-4">
                <ColorPicker
                  defaultColor={formValues.down_sell_button_color}
                  onValueChange={(value) => setValue('down_sell_button_color', value)}
                  label={t('form.color_bg')}
                />
                <ColorPicker
                  defaultColor={formValues.down_sell_display_text_color}
                  onValueChange={(value) => setValue('down_sell_display_text_color', value)}
                  label={t('form.color_text')}
                />
              </div>
            </div>

            <div className="mt-4 p-4 rounded-lg border border-[#27272a] bg-[#09090b]">
              <p className="text-xs text-zinc-500 mb-3 text-center">Preview</p>
              <div className="space-y-2">
                <button
                  type="button"
                  className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: formValues.up_sell_button_color,
                    color: formValues.up_sell_display_text_color,
                  }}
                >
                  {formValues.up_sell_display_text || 'Aceitar'}
                </button>
                <button
                  type="button"
                  className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: formValues.down_sell_button_color,
                    color: formValues.down_sell_display_text_color,
                  }}
                >
                  {formValues.down_sell_display_text || 'Recusar'}
                </button>
              </div>
              {offerSelected.name && (
                <div className="mt-3 pt-3 border-t border-[#27272a]">
                  <p className="text-xs text-zinc-400 text-center">{offerSelected.name}</p>
                  {offerSelected.price && (
                    <p className="text-sm font-semibold text-emerald-400 text-center mt-1">
                      {offerSelected.price}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  const stepLabels = [
    t('step1', 'Informações básicas'),
    t('step2_first', 'Produto') + ' & ' + t('step2_gree_second', 'oferta'),
    t('step3_first', 'Redirecionamento'),
    t('step4', 'Personalização'),
  ];

  return (
    <section className="h-full flex flex-col">
      {/* Header com título e barra de progresso */}
      <div className="relative flex-shrink-0">
        <div className="flex items-center justify-between px-6 my-6">
          <div className="flex gap-4 text-lg font-medium text-zinc-100">
            {t('create_strategy', 'Criar Upsell')}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#18181B]">
          <div
            className="h-full bg-primary transition-all duration-300 ease-out"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Indicador de steps com dots */}
      <div className="px-6 py-4 flex-shrink-0">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold transition-all duration-300 flex-shrink-0 ${
                  s < step
                    ? 'bg-primary text-black'
                    : s === step
                    ? 'bg-primary/20 border border-primary text-primary'
                    : 'bg-[#18181B] border border-[#27272a] text-zinc-600'
                }`}
              >
                {s < step ? <Check className="h-3 w-3" /> : s}
              </div>
              {s < 4 && (
                <div
                  className={`flex-1 h-px transition-colors duration-300 ${
                    s < step ? 'bg-primary' : 'bg-[#27272a]'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <p className="text-xs text-zinc-400 mt-2">{stepLabels[step - 1]}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown} className="flex flex-col flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto px-6 py-2">{renderStep()}</div>

        <div className="flex items-center justify-between p-4 border-t flex-shrink-0 max-sm:pb-12">
          <Button
            type="button"
            variant="ghost"
            onClick={step === 1 ? handleClose : prevStep}
            className="text-zinc-400 hover:text-zinc-100"
          >
            {step === 1 ? t('cancel', 'Cancelar') : t('back', 'Voltar')}
          </Button>
          {step < totalSteps ? (
            <Button type="button" onClick={nextStep} className="px-6">
              {t('advance', 'Avançar')}
            </Button>
          ) : (
            <Button disabled={isLoading} type="submit" className="px-6">
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin h-4 w-4" />
                  {t('common:saving', 'Salvando...')}
                </span>
              ) : (
                t('finish', 'Finalizar')
              )}
            </Button>
          )}
        </div>
      </form>
    </section>
  );
}
